// The People table's filters and sort as the URL's search: codes and numbers only, never free text (docs/CONTRACTS.md
// section 5). A name typed in the filter bar is not here: it travels in the POST body and lives in the screen's state.
// No imports, so the route entry (loaded with the shell) stays small; the screens load as their own chunks.

export const FAMILIES = ['tusen', 'betong', 'sauda', 'tommy'] as const
export const SORTS = ['score', 'name', 'seats'] as const
export const SIZES = ['micro', 'small', 'medium', 'large'] as const
export const TENURES = ['new', 'established'] as const
export type Family = (typeof FAMILIES)[number]

/** The offer family the score is for when the URL names none. */
export const DEFAULT_FAMILY: Family = 'tusen'

/** The table's filters and sort. They are the URL's search (codes only, never free text). */
export interface PeopleFilters {
  family?: Family
  sort?: (typeof SORTS)[number]
  /** one role code: DAGL, LEDE, … */
  role?: string
  /** a NACE division as the facets count it: '23' */
  industry?: string
  /** a county code: '46' */
  county?: string
  size?: (typeof SIZES)[number]
  /** at least this many current seats */
  seats?: number
  /** new: a current seat first seen after the first load; established: none */
  tenure?: (typeof TENURES)[number]
}

const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined => (typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : undefined)
/** The router reads a bare `industry=23` as the number 23 (and writes the string "23" quoted); both give the code '23'. */
const code = (re: RegExp, v: unknown): string | undefined => {
  const text = typeof v === 'number' ? String(v) : v
  return typeof text === 'string' && re.test(text) ? text : undefined
}

/** Router validateSearch: keeps only the filter codes this screen knows, in their valid form. */
export function parseFilters(search: Record<string, unknown>): PeopleFilters {
  const out: PeopleFilters = {}
  const family = oneOf(FAMILIES, search.family)
  const sort = oneOf(SORTS, search.sort)
  const role = code(/^[A-Z]{4}$/, search.role)
  const industry = code(/^\d{2}(?:\.\d{1,3})?$/, search.industry)
  const county = code(/^\d{2}$/, search.county)
  const size = oneOf(SIZES, search.size)
  const seats = Number(code(/^\d{1,2}$/, search.seats))
  const tenure = oneOf(TENURES, search.tenure)
  if (family) out.family = family
  if (sort) out.sort = sort
  if (role) out.role = role
  if (industry) out.industry = industry
  if (county) out.county = county
  if (size) out.size = size
  if (seats >= 1 && seats <= 50) out.seats = seats
  if (tenure) out.tenure = tenure
  return out
}

/** The person page's search: the family whose score it opens on (the table's), else the person's best. */
export function parsePersonSearch(search: Record<string, unknown>): { family?: Family } {
  const family = oneOf(FAMILIES, search.family)
  return family ? { family } : {}
}
